import { formatStorageItemsCountMessage } from '../hooks/useStorageItemsCount'
import { StorageItemIdentity } from '../types'

const ANIMATION_SHARE_POSITION = [0, 6, -6]
const ANIMATION_PROJECT_POSITION = [0, 9]
const ANIMATION_LIST_POSITION = [0, 9, -9]

type DNDGhostStyle = 'list' | 'grid'

const formatWrapperFromReferenceElement = (
  referenceElement: HTMLElement,
  innerWrapper: HTMLElement
) => {
  const { width } = referenceElement.getBoundingClientRect()

  innerWrapper.style.width = `${width}px`
  innerWrapper.style.transform = 'scale(0.75)'
  innerWrapper.style.transformOrigin = 'bottom'
}

const getShareElement = (item?: StorageItemIdentity) => {
  const element = document.querySelector(
    `[data-share-id="${item?.identifier}"]`
  )
  return element?.querySelector('[data-drag-image]') || element
}

const getProjectElement = (item?: StorageItemIdentity) => {
  const element = document.querySelector(
    `[data-project-id="${item?.identifier}"]`
  )
  return element?.querySelector('[data-drag-image]') || element
}

/**
 * Selects up to 2 projects and 3 shares from the given list of storage items
 * to be displayed in the drag-and-drop ghost element.
 */
const selectProjectAndShareElements = (
  selectedItems: StorageItemIdentity[]
) => {
  const projects = []
  const shares = []

  for (const item of selectedItems) {
    if (projects.length + shares.length >= 5) {
      break
    }

    if (projects.length === 2 && item.__typename === 'Project') {
      continue
    }

    if (shares.length === 3 && item.__typename === 'Share') {
      continue
    }

    if (item.__typename === 'Project') {
      projects.push(item)
    } else if (item.__typename === 'Share') {
      shares.push(item)
    }
  }

  return { projects, shares }
}

const renderProjectElement = (
  element: HTMLElement,
  style: DNDGhostStyle,
  index: number = 0,
  selected: boolean = false
) => {
  const dragAndDropElement = element?.cloneNode(true) as HTMLElement
  dragAndDropElement.classList.add('drag-and-drop-ghost', style)

  if (selected) {
    dragAndDropElement.classList.add('selected')
  }

  const animationPosition =
    style === 'grid' ? ANIMATION_PROJECT_POSITION : ANIMATION_LIST_POSITION

  dragAndDropElement.style.transform = `rotate(${animationPosition[index]}deg)`
  dragAndDropElement.style.paddingBottom = '0px'

  return dragAndDropElement
}

const renderShareElement = (
  element: HTMLElement,
  style: DNDGhostStyle,
  index: number = 0,
  selected: boolean = false
) => {
  const dragAndDropElement = element?.cloneNode(true) as HTMLElement
  dragAndDropElement.classList.add('drag-and-drop-ghost', style)

  if (selected) {
    dragAndDropElement.classList.add('selected')
  }

  const animationPosition =
    style === 'grid' ? ANIMATION_SHARE_POSITION : ANIMATION_LIST_POSITION

  dragAndDropElement.style.transform = `rotate(${animationPosition[index]}deg)`

  return dragAndDropElement
}

export const createDnDGhost = (element: HTMLElement, style: DNDGhostStyle) => {
  const wrapper = document.createElement('div')
  const innerWrapper = document.createElement('div')
  innerWrapper.classList.add('drag-and-drop-wrapper', style)

  const itemWrapper = document.createElement('div')
  itemWrapper.classList.add('drag-and-drop-item-wrapper')

  const items: StorageItemIdentity[] = element.dataset.draggedRelatedItems
    ? JSON.parse(element.dataset.draggedRelatedItems || '[]')
    : []

  /**
   * If there's only one item being dragged, we just clone and return that element
   * with minimal style formatting
   */
  if (style === 'grid' && items.length === 1) {
    const { __typename } = items[0]

    if (__typename === 'Project') {
      itemWrapper.appendChild(renderProjectElement(element, style))
    } else if (__typename === 'Share') {
      itemWrapper.appendChild(renderShareElement(element, style))
    }

    formatWrapperFromReferenceElement(element, itemWrapper)

    innerWrapper.appendChild(itemWrapper)
    wrapper.appendChild(innerWrapper)
    return wrapper
  }

  /**
   * If there's more than one item we collect the shares and projects by
   * the data-[element]-id, apply some style to them and the count message
   */
  const { shares, projects } = selectProjectAndShareElements(items)

  projects.forEach((item, index) => {
    const element = getProjectElement(item)

    if (!element) {
      return
    }

    itemWrapper.prepend(
      renderProjectElement(element as HTMLElement, style, index, true)
    )
  })

  shares.forEach((item, index) => {
    const element = getShareElement(item)

    if (!element) {
      return
    }

    const accumulatedIndex = style === 'list' ? index + projects.length : index

    itemWrapper.prepend(
      renderShareElement(element as HTMLElement, style, accumulatedIndex, true)
    )
  })

  // Format the count label
  const { message } = formatStorageItemsCountMessage(items)

  const text = document.createElement('span')
  text.append(document.createTextNode(message))
  text.classList.add('drag-and-drop-text')

  const textWrapper = document.createElement('div')
  textWrapper.classList.add('drag-and-drop-text-wrapper', style)
  textWrapper.appendChild(text)

  /**
   * Format the wrapper based of the elements height
   * Shares take precedence so the drag and drop ghost is 75% of its size
   * if not available we use the folder
   */
  const share = getShareElement(shares[0])
  const project = getProjectElement(projects[0])
  const referenceElement = share || project

  if (referenceElement instanceof HTMLElement && style === 'grid') {
    formatWrapperFromReferenceElement(referenceElement, itemWrapper)
  }

  innerWrapper.appendChild(itemWrapper)
  innerWrapper.appendChild(textWrapper)

  wrapper.appendChild(innerWrapper)

  return wrapper
}
